import { useContext, useEffect, useState } from 'react';
import { Col, Form } from 'react-bootstrap';
import { useSelector } from 'react-redux';
import * as yup from 'yup';
import { paisesSelector } from '../../../appStore/commonSlice';
import { CampoFormik } from '../../../components/CompUtils';
import es from '../../../yup-es';
import { cvTiposSelector } from '../cvSlice';
import { SeccionContext } from '../itemTypes';
import FormikContainer from './FormikContainer';

yup.setLocale(es);

//
// ─── FORMULARIO ITEMS ACADEMICOS ────────────────────────────────────────────────
//

const AcademicosSchema = yup
  .object({
    idAntecedenteAcademico: yup.number(), //si existe, es edicion
    idTipoAntecedenteAcademico: yup.number().required(),
    idPais: yup.number().required(),
    acreditacionObtenida: yup.string().required(),
    establecimiento: yup.string().required(),
    matriculaHabilitante: yup.string(),
    homologado: yup.boolean().required(),
    nroActoAdministrativo: yup.string().when(['homologado', 'idPais'], {
      is: (homologado, idPais) => homologado && idPais !== 13,
      then: yup.string().required(),
    }),
  })
  .shape(
    {
      actualidad: yup.boolean(),
      f_ingreso: yup
        .date()
        .required('La fecha de ingreso es requerida')
        .test('valid-date', 'Fecha de ingreso inválida', (value) => {
          if (value) {
            const year = value.getFullYear().toString();
            const month = (value.getMonth() + 1).toString().padStart(2, '0');
            const day = value.getDate().toString().padStart(2, '0');
            return (
              year >= '1000' &&
              year <= '9999' &&
              month >= '01' &&
              month <= '12' &&
              day >= '01' &&
              day <= '31'
            );
          }
          return true;
        }),
      f_egreso: yup
        .date()
        .nullable()
        .transform((curr, orig) => (orig === '' ? null : curr))
        .when('actualidad', {
          is: false,
          then: yup.date().required('La fecha de egreso es requerida'),
        })
        .when(
          'f_ingreso',
          (ingreso, schema) =>
            ingreso &&
            yup
              .date()
              .test('valid-date', 'Fecha de egreso inválida', (value) => {
                if (value) {
                  const year = value.getFullYear().toString();
                  const month = (value.getMonth() + 1).toString().padStart(2, '0');
                  const day = value.getDate().toString().padStart(2, '0');
                  return (
                    year >= '1000' &&
                    year <= '9999' &&
                    month >= '01' &&
                    month <= '12' &&
                    day >= '01' &&
                    day <= '31'
                  );
                }
                return true;
              })
              .min(yup.ref('f_ingreso'), 'Rango de fechas incorrecto'),
        ),
    },
    [('f_ingreso', 'f_egreso', 'actualidad')],
  );

export const FormAcademicos = () => {
  const formState = useContext(SeccionContext);
  const tiposAcademicos = useSelector(cvTiposSelector).tiposAcademicos;
  const paises = useSelector(paisesSelector);

  const [egresoDisabled, setEgresoDisabled] = useState(false);
  const [showNroActo, setShowNroActo] = useState(false);
  const [showHomologado, setShowHomologado] = useState(false);

  useEffect(() => {
    setEgresoDisabled(formState.initialValues.actualidad && !formState.initialValues.f_egreso);
    if (formState.initialValues.idPais) {
      const esArg = paises && paises[formState.initialValues.idPais].nombre === 'Argentina';
      setShowHomologado(!esArg);
      setShowNroActo(formState.initialValues.homologado && !esArg);
    } else {
      setShowHomologado(false); //No hay pais definido, por defecto no lo muestro
    }
  }, [formState.initialValues, paises]);

  const handleHomologado = (val, formik) => {
    formik.setFieldValue('homologado', val);
    setShowNroActo(val);

    if (!val) {
      formik.setFieldValue('nroActoAdministrativo', '');
    }
  };

  return (
    <FormikContainer
      schema={AcademicosSchema}
      seccionKey="antecedentesAcademicos"
      seccionTitle="Antecedentes Académicos">
      {(formik) => {
        return (
          <>
            <Form.Row>
              <Form.Group as={Col}>
                <Form.Label for="nivelAcreditacion">
                  Nivel de acreditación (obligatorio):
                </Form.Label>
                <Form.Control
                  aria-required
                  id="nivelAcreditacion"
                  as="select"
                  value={formik.values.idTipoAntecedenteAcademico}
                  name="idTipoAntecedenteAcademico"
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  isInvalid={
                    formik.touched.idTipoAntecedenteAcademico &&
                    !!formik.errors.idTipoAntecedenteAcademico
                  }>
                  <option value="">Seleccione</option>
                  {Object.keys(tiposAcademicos).map((key) => (
                    <option value={tiposAcademicos[key].id} key={tiposAcademicos[key].id}>
                      {tiposAcademicos[key].nombre}
                    </option>
                  ))}
                </Form.Control>
                <Form.Control.Feedback type="invalid">
                  {formik.errors.idTipoAntecedenteAcademico}
                </Form.Control.Feedback>
              </Form.Group>
            </Form.Row>
            <Form.Row>
              <CampoFormik
                aria-required
                label="Denominación del título educativo (obligatorio)"
                name="acreditacionObtenida"
                formik={formik}
                help="Deberá cargarse la denominación completa sin abreviaturas."
              />
              <Form.Text className="help-block" style={{ fontSize: 12, marginTop: '-10px' }} muted>
                En caso de que los estudios no estuvieran finalizados, deberán adjuntar el
                certificado de materias aprobadas emitidas por Institución Educativa. En caso de no
                contar con el título obtenido se podrá adjuntar el Certificado de Título en Trámite.
                <br />
                <span className="font-weight-bold">Aclaración: </span> Si ingresó antes del 1° de
                diciembre del 2008 bajo la modalidad de Art. 9 o Designación Transitoria en el
                ámbito del SINEP y aún no ha finalizado sus estudios secundarios deberá adjuntar la
                carta compromiso de finalización de estudios secundarios.
              </Form.Text>
            </Form.Row>
            <Form.Row>
              <CampoFormik
                aria-required
                label="Establecimiento que emite la titulación (obligatorio)"
                name="establecimiento"
                formik={formik}
              />
            </Form.Row>
            <Form.Row>
              <CampoFormik
                aria-required
                type="pais"
                label="País (obligatorio)"
                name="idPais"
                formik={formik}
                onChange={(ev) => {
                  if (paises[ev.target.value].nombre === 'Argentina') {
                    handleHomologado(true, formik);
                    formik.setFieldValue('nroActoAdministrativo', '');
                    setShowHomologado(false);
                    setShowNroActo(false);
                  } else {
                    handleHomologado(false, formik);
                    setShowHomologado(true);
                  }
                  formik.handleChange(ev);
                }}
              />
            </Form.Row>
            <Form.Row>
              {showHomologado && (
                <Form.Group as={Col} sm={5}>
                  <Form.Label for="homologado">Homologado en Argentina (obligatorio)</Form.Label>
                  {[
                    { label: 'SI', value: true },
                    { label: 'NO', value: false },
                  ].map((el) => (
                    <Form.Check
                      aria-required
                      id="homologado"
                      inline
                      type="radio"
                      key={el.label}
                      label={el.label}
                      name="homologado"
                      onBlur={formik.handleBlur}
                      checked={el.value === formik.values.homologado}
                      onChange={(ev) => {
                        handleHomologado(el.value, formik);
                      }}
                    />
                  ))}
                </Form.Group>
              )}
              {showNroActo && (
                <CampoFormik
                  aria-required
                  name="nroActoAdministrativo"
                  label="N° de acto administrativo"
                  col={{ sm: 7 }}
                  formik={formik}
                />
              )}
            </Form.Row>
            <Form.Row>
              <CampoFormik
                label="Matrícula habilitante vigente"
                name="matriculaHabilitante"
                formik={formik}
                help="Se deberá completar el número de la matrícula con tomo y folio."
              />
            </Form.Row>
            <Form.Row>
              <CampoFormik
                aria-required
                label="Fecha de ingreso (obligatorio)"
                name="f_ingreso"
                col={{ sm: 4 }}
                type="date2"
                maxDate={formik.values.f_egreso}
                formik={formik}
              />
              <fieldset style={{ display: 'contents' }} disabled={egresoDisabled}>
                <CampoFormik
                  aria-required
                  label="Fecha de egreso (obligatorio)"
                  name="f_egreso"
                  type="date2"
                  col={{ sm: 4 }}
                  minDate={formik.values.f_ingreso}
                  formik={formik}
                />
              </fieldset>
              <Form.Group>
                <Form.Label for="actualidad">&nbsp;</Form.Label>
                <Form.Check
                  id="actualidad"
                  style={{ transform: 'translateY(25%)' }}
                  type="checkbox"
                  label="Actualidad"
                  name="actualidad"
                  checked={formik.values.actualidad}
                  onBlur={formik.handleBlur}
                  onChange={(ev) => {
                    formik.setFieldValue('actualidad', !formik.values.actualidad);
                    if (ev.target.checked) {
                      formik.setFieldValue('f_egreso', '');
                      setEgresoDisabled(true);
                    } else {
                      setEgresoDisabled(false);
                    }
                    formik.validateField('f_egreso'); //Para validar required o no segun el caso
                  }}
                />
              </Form.Group>
            </Form.Row>
          </>
        );
      }}
    </FormikContainer>
  );
};
